import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from '@tanstack/react-query'
import cx from "classnames";
import { useLocalStorage } from "@uidotdev/usehooks";

import Chart from "./chart";
import { getDateRange } from "./date-range-picker";
import { getChildren } from "./utils";
import BackArrow from "./back-arrow";
import { useAppContext } from "./app-context";

import styles from "./dashboard.module.css";
import ChartEditor from "./chart-editor";
import { fetchDashboardCards, saveDashboardCards } from "./api";
import DateRangePicker from "./date-range-picker";

const GettingStarted = ({ onSignup, onLogin, onAddAccount }) => {
    return (
        <div className={styles.gettingStarted}>
            <h1>Getting started</h1>
            <p>To get started with Budget Butler, connect to your bank.</p>
            <p>Budget Butler uses TrueLayer & GoCardless, which are FSA regulated Open Banking Providers.</p>
            <p>We use data from your accounts to provide useful insights for you about your finances. We never share it with third parties.</p>
            { onSignup &&
                <button
                    onClick={() => onSignup()}
                >
                    Sign up
                </button>
            }
            { onLogin &&
                <button
                    onClick={() => onLogin()}
                >
                    Log in
                </button>
            }
            { onAddAccount &&
                <button
                    onClick={() => onAddAccount()}
                >
                    Add a bank account
                </button>
            }
        </div>
    );
};

const DashboardCard = ({
    activeAccounts,
    dateRangeConfig,
    config,
    onEditClicked,
    editing,
    spaceScopedFetch
}) => {
    const { start, end } = useMemo(() => getDateRange(dateRangeConfig), [dateRangeConfig]);

    return (
        <div className={cx(styles.card, {
            [ styles.editing ]: editing
        })}>
            <h3 className={styles.title}>
                <span>{config.title}</span>
                { onEditClicked &&
                    <button
                        onClick={() => onEditClicked()}
                        className="material-icons"
                    >edit</button>
                }
            </h3>
            <Chart
                start={start}
                end={end}
                activeAccounts={activeAccounts}
                config={config}
                spaceScopedFetch={spaceScopedFetch}
            ></Chart>
        </div>
    );
};

const checkIfDateRangeIsValid = (dateRange) => {
    if (!dateRange) {
        return false;
    }
    if(!dateRange.option) {
        return false;
    }
    if(dateRange.option === "custom" && (!dateRange.start || !dateRange.end)) {
        return false;
    }
    return true;
}

const useTransformedValue = (value, setValue, inTransformer, outTransformer, dependencies) => {
    const transformedValue = useMemo(() => inTransformer(value), dependencies);
    const setTransformedValue = newValue => setValue(outTransformer(newValue));
    return [ transformedValue, setTransformedValue ];
};

const useDateRangeConfig = () => {
    const [ dateRangeConfig, setDateRangeConfig ] = useLocalStorage("dateRangeConfig", { option: "last7days" });
    return useTransformedValue(
        dateRangeConfig,
        setDateRangeConfig,
        storedValue => {
            if (storedValue.option === "custom") {
                return {
                    ...storedValue,
                    start: storedValue.start ? new Date(storedValue.start) : null,
                    end: storedValue.end ? new Date(storedValue.end) : null
                };
            }
            return storedValue;
        },
        newValue => {
            if (newValue.option === "custom") {
                return {
                    ...newValue,
                    start: newValue.start ? newValue.start.toISOString() : null,
                    end: newValue.end ? newValue.end.toISOString() : null
                };
            }
            return newValue;
        },
        [ dateRangeConfig.start, dateRangeConfig.end, dateRangeConfig.option ]
    );
}

export default () => {
    const { accounts, activeAccounts, onAddAccount, categoriesQuery: { data: categories }, spaceScopedFetch } = useAppContext();
    const [ topLevelCategories, setTopLevelCategories ] = useState(null);

    const [ editingCardUri, setEditingCardUri ] = useState(null);
    const [ editingCard, setEditingCard ] = useState(null);
    const [ dateRangeConfig, setDateRangeConfig ] = useDateRangeConfig();

    const dateRangeIsValid = checkIfDateRangeIsValid(dateRangeConfig);

    useEffect(() => {
        if (!dateRangeIsValid) {
            setDateRangeConfig({ option: "last7days" });
        }
    }, [ dateRangeIsValid ]);

    useEffect(() => {
        if (!topLevelCategories && categories) {
            const categoriesByUri = Object.fromEntries(categories.map(c => [c.uri, c]));
            const topLevelCategories = getChildren(categoriesByUri, null);
            setTopLevelCategories(topLevelCategories.map(c => c.uri));
        }
    }, [categories]);

    const {
        data: cardsData,
        refetch: refetchCards,
    } = useQuery({
        queryKey: [ "dashboardCards", spaceScopedFetch.ident ],
        queryFn: () => fetchDashboardCards(spaceScopedFetch),
        enabled: !!spaceScopedFetch,
        select: cards => ({
            cards: Object.fromEntries(cards.map(c => [c.uri, c])),
            cardsOrder: cards.map(c => c.uri)
        })
    });

    const {
        cards,
        cardsOrder
    } = cardsData || { cards: {}, cardsOrder: [] };

    if(!spaceScopedFetch) {
        return null;
    }

    if (accounts?.length === 0) {
        return (
            <GettingStarted onAddAccount={onAddAccount} />
        );
    }

    const cardProps = {
        categories,
        activeAccounts,
        dateRangeConfig,
        spaceScopedFetch: spaceScopedFetch,
        className: styles.chart
    };

    const cardsToRender = cardsOrder
        .map(uri => uri === editingCardUri ? {
            ...cards[uri],
            config: editingCard
        } : cards[uri])
        .filter(v => !!v);

    if(editingCard && !editingCardUri) {
        cardsToRender.unshift({
            uri: `new:${Date.now().toString()}`,
            config: editingCard
        });
    }

    const renderAddChartButtons = () => {
        const templateCharts = [{
            cta: "Create new bar chart",
            config: {
                type: "bar",
                cumulative: false,
                transactionType: "debit",
                title: "Spending by category",
                xaxisField: "category",
                yaxisBreakdown: "category",
                xaxisCategories: topLevelCategories,
                yaxisCategories: topLevelCategories,
                filterAllCategories: true
            }
        }, {
            cta: "Create new line chart",
            config: {
                type: "line",
                cumulative: false,
                transactionType: "debit",
                title: "Spending over time",
                xaxisField: "timestamp",
                yaxisBreakdown: "category",
                timestampBuckets: "date",
                xaxisCategories: topLevelCategories,
                yaxisCategories: topLevelCategories,
                filterAllCategories: true
            }
        }, {
            cta: "Create new pie chart",
            config: {
                id: 1,
                type: "doughnut",
                title: "Spending breakdown",
                transactionType: "debit",
                elementsBreakdown: "category",
                categories: topLevelCategories,
                filterAllCategories: true
            }
        }];
        return (
            <div className={styles.createChartButtons}>
                { templateCharts.map(template =>
                    <button
                        key={template.cta}
                        onClick={() => {
                            setEditingCard(template.config);
                        }}
                        disabled={!!editingCard}
                    >
                        <span>{template.cta}</span>
                    </button>
                )}
            </div>
        );
    };

    if(!dateRangeIsValid) {
        return null;
    }

    return (
        <div className={cx(styles.root, { [ styles.sideBarCollapsed ]: !editingCard })}>
            <div className={styles.mainContent}>
                <h1><BackArrow /><span className="material-symbols-outlined">bar_chart_4_bars</span><span>At a glance</span></h1>
                <DateRangePicker
                    dateRangeConfig={dateRangeConfig}
                    onDateRangeConfigChange={setDateRangeConfig}
                />
                { renderAddChartButtons() }
                <div className={styles.dashboard}>
                    { cardsToRender
                        .map(card => 
                            <DashboardCard
                                key={card.uri}
                                config={card.config}
                                onEditClicked={!editingCard && (() => {
                                    setEditingCardUri(card.uri)
                                    setEditingCard(card.config);
                                })}
                                editing={editingCard === card.config}
                                { ... cardProps }
                            ></DashboardCard>
                        )
                    }
                </div>
            </div>
            <div className={styles.sidebar}>
                <ChartEditor
                    config={editingCard}
                    categories={categories}
                    onConfigChanged={newConfig => setEditingCard(newConfig)}
                    onSave={async () => {
                        await saveDashboardCards(cardsToRender, spaceScopedFetch);
                        await refetchCards();
                        setEditingCard(null);
                        setEditingCardUri(null);
                    }}
                    onCancel={() => {
                        setEditingCard(null);
                        setEditingCardUri(null);
                    }}
                    onDelete={async () => {
                        const newCards = cardsToRender.map(c => c.uri === editingCardUri ? {
                            ...c,
                            deleted: true
                        } : c);
                        await saveDashboardCards(newCards, spaceScopedFetch);
                        await refetchCards();
                        setEditingCard(null);
                        setEditingCardUri(null);
                    }}
                />
            </div>
        </div>
    );
};
